import {
  darken,
  mode,
  StyleFunctionProps,
  transparentize,
} from "@chakra-ui/theme-tools";
import { colors } from "../colors";
import { fontSizes } from "../sizes";

const chakraBaseStyle = {
  ":focus:not(:focus-visible)": {
    boxShadow: "none",
  },
  fontWeight: "semibold",
  borderRadius: "lg",
};

const baseStyle = {
  ...chakraBaseStyle,
  _active: {
    boxShadow: "none",
  },
  _disabled: {
    boxShadow: "none",
    opacity: 0.4,
  },
  borderRadius: "lg",
};

const chakraSizes = {
  lg: {
    fontSize: "md",
  },
  xl: {
    h: "3.75rem",
    minW: "3.75rem",
    fontSize: "lg",
    px: 7,
  },
};

const sizes = {
  xl: { ...chakraSizes.xl, fontSize: fontSizes.x4l },
  lg: { ...chakraSizes.lg, fontSize: fontSizes.x3l, fontWeight: "bold" },
  md: { fontSize: fontSizes.lg, h: "3rem" },
  sm: { fontSize: fontSizes.md },
  xs: { fontSize: fontSizes.x2s },
  "th-md": {
    fontSize: "1rem",
    minH: "54px",
  },
  "th-slim": {
    fontSize: "1rem",
    minH: "38px",
  },
  "th-button-link-lg": {
    fontSize: "1.125rem",
    minH: "unset",
  },
};

const chakraVariants = {
  primary: (props: StyleFunctionProps) =>
    props.theme.components["Button"]["variants"]["solid"]({
      ...props,
      variant: "solid",
      colorScheme: "brand",
    }),
  "primary-on-accent": () => ({
    bg: "brand.50",
    color: "brand.600",
    _hover: { bg: "brand.100" },
    _active: { bg: "brand.100" },
  }),
  secondary: (props: StyleFunctionProps) =>
    props.theme.components["Button"]["variants"]["outline"]({
      ...props,
      variant: "outline",
      colorScheme: "gray",
    }),
  "secondary-on-accent": {
    color: "white",
    borderColor: "brand.50",
    borderWidth: "1px",
    _hover: { bg: "whiteAlpha.200" },
    _active: { bg: "whiteAlpha.200" },
  },
  outline: (props: StyleFunctionProps) => ({
    color: "emphasized",
    bg: mode("white", "gray.800")(props),
    _hover: {
      bg: mode(
        darken("gray.50", 1)(props.theme),
        transparentize("gray.700", 0.4)(props.theme),
      )(props),
    },
    _checked: {
      bg: mode("gray.100", "gray.700")(props),
    },
    _active: {
      bg: mode("gray.100", "gray.700")(props),
    },
  }),
  ghost: (props: StyleFunctionProps) => ({
    color: "emphasized",
    _hover: {
      bg: mode(
        darken("gray.50", 1)(props.theme),
        darken("gray.700", 4)(props.theme),
      )(props),
    },
    _active: {
      bg: mode(
        darken("gray.50", 1)(props.theme),
        darken("gray.700", 4)(props.theme),
      )(props),
    },
    _activeLink: {
      bg: mode("gray.100", "gray.700")(props),
    },
  }),
  "ghost-on-accent": (props: StyleFunctionProps) => ({
    color: "brand.50",
    _hover: {
      bg: transparentize("brand.500", 0.67)(props.theme),
    },
    _activeLink: {
      color: "white",
      bg: "bg-accent-subtle",
    },
  }),
  link: (props: StyleFunctionProps) => {
    if (props.colorScheme === "gray") {
      return {
        color: "muted",
        _hover: {
          textDecoration: "none",
          color: "default",
        },
        _active: {
          color: "default",
        },
      };
    }
    return {
      color: mode(
        `${props.colorScheme}.600`,
        `${props.colorScheme}.200`,
      )(props),
      _hover: {
        color: mode(
          `${props.colorScheme}.700`,
          `${props.colorScheme}.300`,
        )(props),
        textDecoration: "none",
      },
      _active: {
        color: mode(
          `${props.colorScheme}.700`,
          `${props.colorScheme}.300`,
        )(props),
      },
    };
  },
  "link-on-accent": () => {
    return {
      padding: 0,
      height: "auto",
      lineHeight: "normal",
      verticalAlign: "baseline",
      color: "brand.50",
      _hover: {
        color: "white",
      },
      _active: {
        color: "white",
      },
    };
  },
  stripe: {
    "--webkit-font-smoothing": "antialiased",
    bgColor: "white",
    border: "1px solid #e6e6e6",
    borderRadius: "5px",
    boxShadow: "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
    color: "rgb(78, 84, 76)",
    fontFamily: "Moderat, sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    justifyContent: "flex-start",
    lineHeight: "18.4px",
    minWidth: { base: "100%", sm: "266px" },
    p: "12px",
  },
};

const backgroundColors = (base: string, hover: string) => ({
  background: base,
  backgroundColor: base,
  _active: {
    background: base,
    backgroundColor: base,
  },
  _disabled: {
    background: base,
    backgroundColor: base,
  },
  _hover: {
    background: hover,
    backgroundColor: hover,
    _disabled: {
      background: base,
      backgroundColor: base,
    },
  },
});

const variants = {
  ...chakraVariants,
  errorOutline: {
    ...backgroundColors("white", "th.pink1"),
    border: `1px solid ${colors.newReddish[200]} !important`,
    color: "newReddish.500",
  },
  outline: (props: StyleFunctionProps) => ({
    bg: "transparent",
    color: `${props.colorScheme}.800`,
    borderColor: `${props.colorScheme}.800`,
    _hover: { bg: `${props.colorScheme}.100` },
    _active: { bg: `${props.colorScheme}.100` },
  }),
  "outline-on-accent": (props: StyleFunctionProps) => ({
    bg: "transparent",
    border: "1px solid",
    borderColor: `${props.colorScheme}.100`,
    color: `${props.colorScheme}.100`,
    _hover: {
      borderColor: `${props.colorScheme}.50`,
      color: `${props.colorScheme}.50`,
    },
    _active: {
      borderColor: `${props.colorScheme}.50`,
      color: `${props.colorScheme}.50`,
    },
  }),
  solid: (props: StyleFunctionProps) => ({
    bg: `${props.colorScheme}.800`,
    color: "white",
    borderColor: `${props.colorScheme}.800`,
    _hover: { bg: `${props.colorScheme}.900` },
    _active: { bg: `${props.colorScheme}.900` },
  }),
  solidBright: (props: StyleFunctionProps) => ({
    bg: `${props.colorScheme}.100`,
    color: "fg-main", // Needed since button text color is white by default
    borderColor: `${props.colorScheme}.100`,
    _hover: { bg: `${props.colorScheme}.50` },
    _active: { bg: `${props.colorScheme}.50` },
  }),
  "primary-new": {
    ...backgroundColors("fg-accent", "fg-accent-emphasized"),
    color: "on-accent",
    fontSize: "1.25rem",
    fontWeight: "black",
    lineHeight: "120%",
  },
  "tertiary-new": {
    ...backgroundColors("newPeach.100", "th.peach2"),
    color: "on-accent",
    fontSize: "1.25rem",
    fontWeight: "black",
    lineHeight: "120%",
  },
  "ghost-new": {
    ...backgroundColors("bg-lighter", "bg-lighter"),
    color: "fg-accent",
    _hover: {
      ...backgroundColors("bg-lighter", "bg-lighter")._hover,
      color: "fg-accent-emphasized",
      _disabled: {
        ...backgroundColors("bg-lighter", "bg-lighter")._hover._disabled,
        color: "fg-accent",
      },
    },
  },
  "secondary-new": {
    ...backgroundColors("th.yellowGreen1", "newBrightGreen.100"),
    color: "fg-accent",
    fontSize: "1.25rem",
    fontWeight: "black",
    lineHeight: "120%",
  },
  // New variants as part of TH design system
  "th-primary": () => ({
    bg: "th.actionLavenderPrimary",
    color: "fg-main",
    fontWeight: "bold",
    letterSpacing: "0.5px",
    px: 6,
    py: 4,
    _active: { bg: "th.actionLavender2" },
    _hover: {
      bg: "th.actionLavender1",
      _disabled: {
        bg: "th.actionLavenderPrimary",
      },
    },
  }),
  "th-secondary": () => ({
    bg: "transparent",
    border: "1px solid",
    borderColor: "fg-main",
    borderRadius: "8px",
    color: "fg-main",
    fontWeight: "normal",
    px: 4,
    py: 2,
    _active: { bg: "th.actionLavender1" },
    _hover: {
      bg: "th.actionLavenderPrimary",
    },
  }),
  "th-text-button": {
    bg: "transparent",
    color: "th.black",
    fontSize: "1.125rem",
    fontWeight: "bold",
    letterSpacing: "0.5px",
    lineHeight: "140%",
    p: 2,
    _active: { bg: "th.actionLavender1" },
    _hover: {
      bg: "th.actionLavenderPrimary",
    },
  },
  "th-button-text": () => ({
    color: "fg-main",
    fontWeight: "semibold",
    h: "unset",
    px: "unset",
    minH: "unset",
    _active: { color: "th.textLavender2" },
    _hover: {
      color: "th.textLavender1",
      _disabled: {
        color: "fg-main",
      },
    },
  }),
  "th-text-link": () => ({
    color: "fg-main",
    fontWeight: "light",
    minH: "unset",
    textDecoration: "underline",
    _active: { color: "th.textLavender2" },
    _hover: {
      color: "th.textLavender1",
      _disabled: {
        color: "fg-main",
      },
    },
  }),
  "th-circle-button": (props: StyleFunctionProps) => ({
    align: "center",
    bg: props.bg,
    border: props.border,
    borderColor: props.borderColor,
    h: 8,
    justify: "center",
    minW: 0,
    px: 0,
    rounded: "full",
    w: 8,
    _active: { bg: "th.actionLavender1" },
    _hover: {
      bg: "th.actionLavenderPrimary",
    },
  }),
  "th-error-outline": {
    bg: "transparent",
    border: "1px solid",
    borderColor: "th.danger1",
    borderRadius: "8px",
    color: "th.danger1",
    fontWeight: "bold",
    px: 4,
    py: 2,
    _active: { bg: "th.danger1Hover" },
    _hover: {
      bg: "th.danger1Hover",
    },
  },
};

export const Button = {
  baseStyle,
  sizes,
  variants,
};
